import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { CheckCircle, Calendar, Heart, Brain, Users, Plus, Waves, Compass, Sun, Sunrise, Moon, Droplets, Utensils, Pill, Activity, Trees, Clock, BarChart3, Wine, Target, Cigarette, Coffee, Smartphone, Settings, Sparkles } from 'lucide-react';
import { SparkleGuide } from './guide/SparkleGuide';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import AlcoholTracker from './AlcoholTracker';
import BehaviorInsights from './BehaviorInsights';
import HabitTracker from './HabitTracker';
import EnhancedSleepTracker from './EnhancedSleepTracker';
import EnhancedExerciseTracker from './EnhancedExerciseTracker';
import WaterTracker from './gamification/WaterTracker';
import SunlightTracker from './gamification/SunlightTracker';
import LifeExpansionCalendar from './LifeExpansionCalendar';
import ConnectedSelf from './ConnectedSelf';
import HabitPreferencesSelector from './HabitPreferencesSelector';
import { useWhoopAutoFill } from '@/hooks/useWhoopAutoFill';
import WhoopAutoFillIndicator from './whoop/WhoopAutoFillIndicator';

interface DailyCheckInProps {
  onComplete?: () => void;
}

interface CheckInData {
  // Check-in timing
  check_in_type?: string;
  morning_completed?: boolean;
  evening_completed?: boolean;
  
  // Energy fields
  energy_level?: number;
  energy_reflection?: string;
  
  // Body fields
  body_feeling?: string;
  tension_location?: string;
  rest_quality?: string;
  movement_today?: string;
  hydration_nourishment?: string;
  
  // Exercise tracking (Enhanced)
  planned_exercise?: string;
  planned_time?: string;
  planned_duration?: number;
  planned_intensity?: number;
  exercise_completed?: boolean;
  actual_exercise?: string;
  actual_duration?: number;
  actual_intensity?: number;
  completion_time?: string;
  weekly_commitment_score?: number;
  personal_promise_kept?: boolean;
  exercise_notes?: string;
  
  // Sleep tracking (Enhanced)
  bedtime?: string;
  wake_time?: string;
  sleep_duration?: number;
  sleep_quality?: number;
  shared_bed?: boolean;
  pet_in_room?: boolean;
  window_open?: boolean;
  night_wakings?: number;
  sleep_factors?: string[];
  sleep_environment?: string;
  
  // Alcohol tracking (Enhanced)
  units_consumed?: number;
  drinks?: Array<{
    type: string;
    amount: number;
    abv: number;
    units: number;
  }>;
  risk_level?: 'green' | 'amber' | 'red';
  alcohol_units?: number;
  alcohol_drinks?: number;
  alcohol_day_type?: 'green' | 'amber' | 'red';
  alcohol_craving_strength?: number;
  alcohol_craving_resisted?: boolean;
  alcohol_notes?: string;
  
  // Habit tracking (Enhanced)
  type?: 'smoking' | 'drugs' | 'caffeine' | 'screen_time' | 'shopping' | 'gaming' | 'custom';
  support_mode?: boolean;
  habit_urges?: any[];
  habit_resistances?: any[];
  
  // Additional emotional and mental fields
  emotion?: string;
  mind_state?: string;
  spiritual_connection?: string;
  future_self_guidance?: string;
  future_self_message?: string;
  future_self_version?: string;
  custom_future_self?: string;
  
  // Exercise tracking (original)
  exercise_duration?: number;
  exercise_type?: string;
  exercise_time_of_day?: string;
  exercise_intensity?: number;
  
  // Sunlight tracking
  morning_sunlight?: boolean;
  sunlight_minutes?: number;
  sunlight_location?: string;
  sunlight_streak?: number;
  
  // Nature time
  nature_time_minutes?: number;
  
  // Sleep tracking (original)
  hours_slept?: number;
  
  // Hydration tracking
  water_intake?: number;
  water_target?: number;
  glass_size?: number;
  
  // Food tracking
  processed_food_level?: number;
  healthy_food_level?: number;
  cravings_level?: number;
  impulsive_eating_level?: number;
  
  // Optional eating disorder support
  eating_disorder_support?: boolean;
  purging_behavior?: boolean;
  restriction_behavior?: boolean;
  laxative_use?: boolean;
  
  // Medications (flexible types for JSONB)
  medications_taken?: any;
  custom_medications?: any;
  
  // Emotion fields  
  emotion_word?: string;
  self_compassion?: string;
  meaningful_connection?: string;
  
  // Mind fields
  headspace_level?: number;
  focus_practice?: string;
  
  // Spirit fields
  presence_practice?: string;
  synchronicities?: string;
  
  // Relational fields
  gratitude_kindness?: string;
  future_self_action?: string;
  
  // Evening ritual fields
  manifestation_focus?: string;
  manifestation_custom?: string;
  regulation_ritual?: string;
  custom_regulation_rituals?: any;
  regulation_rituals_completed?: any;
  intuitive_nudge?: string;
  
  // Life Expansion fields
  life_expansion_planned_activity?: string;
  life_expansion_activity_category?: string;
  life_expansion_difficulty?: string;
  life_expansion_activity_custom?: string;
  life_expansion_social_goal?: string;
  life_expansion_social_person?: string;
  life_expansion_opposite_emotion?: string;
  life_expansion_motivation_hack?: string;
  life_expansion_completed?: boolean;
  life_expansion_completion_status?: string;
  life_expansion_how_it_felt?: string;
  life_expansion_barrier?: string;
  life_expansion_barrier_solution?: string;
  life_expansion_streak_count?: number;
  life_expansion_total_activities_added?: number;
  values_activity_alignment?: string;
  values_identified?: any;
}

export default function DailyCheckIn({ onComplete }: DailyCheckInProps) {
  const [formData, setFormData] = useState<CheckInData>({});
  const [isLoading, setIsLoading] = useState(false);
  const [hasCompletedToday, setHasCompletedToday] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [checkInType, setCheckInType] = useState<'morning' | 'evening'>('morning');
  const [showConfetti, setShowConfetti] = useState(false);
  const [customMeds, setCustomMeds] = useState<string[]>([]);
  const [customRituals, setCustomRituals] = useState<string[]>([]);
  
  // Habit preferences state
  const [habitPreferences, setHabitPreferences] = useState<string[]>([]);
  const [showPreferencesSelector, setShowPreferencesSelector] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  
  // Life Expansion state
  const [activityCategory, setActivityCategory] = useState<'pleasant' | 'people' | 'purpose'>('pleasant');
  const [activityDifficulty, setActivityDifficulty] = useState<'micro' | 'small' | 'stretch'>('micro');
  const [plannedActivity, setPlannedActivity] = useState('');
  const [socialPerson, setSocialPerson] = useState('');
  const [socialGoal, setSocialGoal] = useState('');
  const [oppositeEmotion, setOppositeEmotion] = useState('');
  const [motivationHack, setMotivationHack] = useState('');
  const [valuesAlignment, setValuesAlignment] = useState('');
  const [completionStatus, setCompletionStatus] = useState('');
  const [howItFelt, setHowItFelt] = useState('');
  const [barrier, setBarrier] = useState('');
  const [barrierSolution, setBarrierSolution] = useState('');
  const [whoopDataApplied, setWhoopDataApplied] = useState(false);
  
  const { toast } = useToast();
  
  // WHOOP Auto-Fill hook
  const { 
    isConnected: whoopConnected, 
    hasData: whoopHasData, 
    autoFillData: whoopAutoFill,
    lastSync: whoopLastSync,
    whoopData 
  } = useWhoopAutoFill();

  // Base morning steps - always include Connected Self
  const baseMorningSteps = ['Connected Self'];
  const habitSteps: { [key: string]: string } = {
    'exercise': 'Exercise & Movement',
    'sleep': 'Enhanced Sleep Tracker',
    'sunlight': 'Sunlight & Nature',
    'hydration': 'Hydration Tracker',
    'nutrition': 'Food & Nutrition',
    'alcohol': 'Alcohol Tracking',
    'smoking': 'Habit Tracker',
    'caffeine': 'Habit Tracker',
    'screen_time': 'Habit Tracker',
    'shopping': 'Habit Tracker',
    'gaming': 'Habit Tracker'
  };

  // Build dynamic morning steps based on preferences
  const buildMorningSteps = () => {
    const steps = [...baseMorningSteps];
    const addedSteps = new Set<string>();
    
    habitPreferences.forEach(prefId => {
      const stepName = habitSteps[prefId];
      if (stepName && !addedSteps.has(stepName)) {
        steps.push(stepName);
        addedSteps.add(stepName);
      }
    });
    
    steps.push('Expanding Your Life'); // Always end with this
    return steps;
  };

  const morningSteps = habitPreferences.length > 0 ? buildMorningSteps() : baseMorningSteps;

  const eveningSteps = [
    'Daily Reflection',
    'Behaviour Insights',
    'Life Expansion Review',
    'Regulation Ritual',
    'Intuitive Wisdom'
  ];

  const steps = checkInType === 'morning' ? morningSteps : eveningSteps;

  useEffect(() => {
    checkTodaysCheckIn();
    loadHabitPreferences();
  }, []);

  // WHOOP Auto-Fill Effect
  useEffect(() => {
    if (whoopHasData && whoopAutoFill.raw && !whoopDataApplied && checkInType === 'morning') {
      // Only auto-fill if fields are empty (don't override user input)
      setFormData(prev => {
        const updates: Partial<CheckInData> = {};
        
        // Sleep fields
        if (!prev.hours_slept && whoopAutoFill.hours_slept) {
          updates.hours_slept = whoopAutoFill.hours_slept;
        }
        if (!prev.sleep_quality && whoopAutoFill.sleep_quality) {
          updates.sleep_quality = whoopAutoFill.sleep_quality;
        }
        if (!prev.sleep_duration && whoopAutoFill.sleep_duration) {
          updates.sleep_duration = whoopAutoFill.sleep_duration;
        }
        
        // Energy/Recovery fields
        if (!prev.energy_level && whoopAutoFill.energy_level) {
          updates.energy_level = whoopAutoFill.energy_level;
        }
        if (!prev.rest_quality && whoopAutoFill.rest_quality) {
          updates.rest_quality = whoopAutoFill.rest_quality;
        }
        
        // Exercise intensity from strain
        if (!prev.exercise_intensity && whoopAutoFill.exercise_intensity) {
          updates.exercise_intensity = whoopAutoFill.exercise_intensity;
        }
        
        if (Object.keys(updates).length > 0) {
          // WHOOP auto-fill applied
          return { ...prev, ...updates };
        }
        return prev;
      });
      
      setWhoopDataApplied(true);
    }
  }, [whoopHasData, whoopAutoFill, whoopDataApplied, checkInType]);

  const loadHabitPreferences = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('daily_checkins')
        .select('habit_preferences')
        .eq('user_id', user.id)
        .order('date', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (!error && data?.habit_preferences && data.habit_preferences.length > 0) {
        setHabitPreferences(data.habit_preferences);
        setIsFirstLoad(false);
      } else {
        // No preferences set - show selector on first load
        setShowPreferencesSelector(true);
      }
    } catch (error) {
      // Error loading preferences - silent fail
    }
  };

  const checkTodaysCheckIn = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const today = new Date().toISOString().split('T')[0];
      const { data, error } = await supabase
        .from('daily_checkins')
        .select('*')
        .eq('user_id', user.id)
        .eq('date', today)
        .maybeSingle();

      if (error) {
        // Error checking today's check-in
        return;
      }

      if (data) {
        // Always load existing data to preserve user inputs
        setFormData(data);
        setCheckInType((data.check_in_type === 'evening' ? 'evening' : 'morning') as 'morning' | 'evening');
        
        // Only set as completed if the current check-in type is actually completed
        if (checkInType === 'morning' && data.morning_completed) {
          setHasCompletedToday(true);
        } else if (checkInType === 'evening' && data.evening_completed) {
          setHasCompletedToday(true);
        } else {
          setHasCompletedToday(false);
        }
      } else {
        // Load most recent data as defaults
        const { data: recentData } = await supabase
          .from('daily_checkins')
          .select('*')
          .eq('user_id', user.id)
          .order('date', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (recentData) {
          // Pre-populate form with recent data as defaults (excluding date-specific fields)
          const defaultData = {
            water_target: recentData.water_target || 2000,
            glass_size: recentData.glass_size || 250,
            cycle_start_date: recentData.cycle_start_date,
            cycle_length: recentData.cycle_length || 28,
            period_end_date: recentData.period_end_date,
            custom_medications: recentData.custom_medications || [],
            custom_regulation_rituals: recentData.custom_regulation_rituals || []
          };
          setFormData(defaultData);
        }
      }
    } catch (error) {
      // Error checking check-in - silent fail
    }
  };

  const handleInputChange = (field: keyof CheckInData, value: string | number | boolean | any[]) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      // Auto-save on every change to prevent data loss
      autoSaveData(newData);
      return newData;
    });
  };

  // Auto-save function with debouncing to avoid too many saves
  const autoSaveData = async (data: CheckInData) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const today = new Date().toISOString().split('T')[0];
      
      const updateData = {
        user_id: user.id,
        date: today,
        check_in_type: checkInType,
        ...data
      };

      // Save to database (silently, no toast notification)
      await supabase
        .from('daily_checkins')
        .upsert(updateData as any, { 
          onConflict: 'user_id,date',
          ignoreDuplicates: false 
        });
        
      // Auto-saved successfully
    } catch (error) {
      // Auto-save error - silent fail
      // Don't show error toast for auto-save to avoid interrupting user experience
    }
  };

  const saveCheckIn = async () => {
    try {
      setIsLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Authentication required",
          description: "Please log in to save your check-in.",
          variant: "destructive"
        });
        return;
      }

      const today = new Date().toISOString().split('T')[0];
      
      // Prepare comprehensive data for saving
      const updateData = {
        user_id: user.id,
        date: today,
        check_in_type: checkInType,
        
        // Energy and body
        energy_level: formData.energy_level,
        energy_reflection: formData.energy_reflection,
        body_feeling: formData.body_feeling,
        tension_location: formData.tension_location,
        rest_quality: formData.rest_quality,
        movement_today: formData.movement_today,
        
        // Hydration and nutrition
        water_intake: formData.water_intake || 0,
        water_target: formData.water_target || 2000,
        glass_size: formData.glass_size || 250,
        processed_food_level: formData.processed_food_level,
        healthy_food_level: formData.healthy_food_level,
        cravings_level: formData.cravings_level,
        impulsive_eating_level: formData.impulsive_eating_level,
        eating_disorder_support: formData.eating_disorder_support || false,
        purging_behavior: formData.purging_behavior || false,
        restriction_behavior: formData.restriction_behavior || false,
        laxative_use: formData.laxative_use || false,
        
        // Sunlight tracking
        morning_sunlight: formData.morning_sunlight || false,
        sunlight_minutes: formData.sunlight_minutes || 0,
        sunlight_location: formData.sunlight_location,
        sunlight_streak: formData.sunlight_streak || 0,
        nature_time_minutes: formData.nature_time_minutes || 0,
        
        // Sleep tracking
        hours_slept: formData.hours_slept,
        sleep_quality: formData.sleep_quality,
        bedtime: formData.bedtime,
        wake_time: formData.wake_time,
        sleep_duration: formData.sleep_duration,
        shared_bed: formData.shared_bed || false,
        pet_in_room: formData.pet_in_room || false,
        window_open: formData.window_open || false,
        night_wakings: formData.night_wakings || 0,
        sleep_factors: formData.sleep_factors || [],
        sleep_environment: formData.sleep_environment,
        
        // Exercise tracking
        exercise_duration: formData.exercise_duration,
        exercise_type: formData.exercise_type,
        exercise_time_of_day: formData.exercise_time_of_day,
        exercise_intensity: formData.exercise_intensity,
        planned_exercise: formData.planned_exercise,
        planned_time: formData.planned_time,
        planned_duration: formData.planned_duration,
        planned_intensity: formData.planned_intensity,
        exercise_completed: formData.exercise_completed || false,
        actual_exercise: formData.actual_exercise,
        actual_duration: formData.actual_duration,
        actual_intensity: formData.actual_intensity,
        completion_time: formData.completion_time,
        weekly_commitment_score: formData.weekly_commitment_score,
        personal_promise_kept: formData.personal_promise_kept || false,
        exercise_notes: formData.exercise_notes,
        
        // Alcohol tracking
        units_consumed: formData.units_consumed || 0,
        drinks: formData.drinks || [],
        risk_level: formData.risk_level || 'green',
        alcohol_units: formData.alcohol_units || 0,
        alcohol_drinks: formData.alcohol_drinks || 0,
        alcohol_day_type: formData.alcohol_day_type || 'green',
        alcohol_craving_strength: formData.alcohol_craving_strength,
        alcohol_craving_resisted: formData.alcohol_craving_resisted || false,
        alcohol_notes: formData.alcohol_notes,
        
        // Habit tracking
        type: formData.type,
        support_mode: formData.support_mode || false,
        habit_urges: formData.habit_urges || [],
        habit_resistances: formData.habit_resistances || [],
        
        // Emotional and mental state
        emotion: formData.emotion,
        emotion_word: formData.emotion_word,
        self_compassion: formData.self_compassion,
        meaningful_connection: formData.meaningful_connection,
        mind_state: formData.mind_state,
        headspace_level: formData.headspace_level,
        focus_practice: formData.focus_practice,
        
        // Spiritual and future self
        presence_practice: formData.presence_practice,
        synchronicities: formData.synchronicities,
        spiritual_connection: formData.spiritual_connection,
        gratitude_kindness: formData.gratitude_kindness,
        future_self_action: formData.future_self_action,
        future_self_guidance: formData.future_self_guidance,
        future_self_message: formData.future_self_message,
        future_self_version: formData.future_self_version,
        custom_future_self: formData.custom_future_self,
        
        // All other form data fields
        ...formData
      };

      // Update completion status
      if (checkInType === 'morning') {
        updateData.morning_completed = true;
      } else {
        updateData.evening_completed = true;
      }

      // Saving check-in data

      const { error } = await supabase
        .from('daily_checkins')
        .upsert(updateData as any, { 
          onConflict: 'user_id,date',
          ignoreDuplicates: false 
        });

      if (error) {
        // Error saving check-in
        throw error;
      }

      // Save sunlight data separately if morning check-in
      if (checkInType === 'morning' && formData.morning_sunlight) {
        const { error: sunlightError } = await supabase
          .from('sunlight_log')
          .upsert({
            user_id: user.id,
            date: today,
            got_sunlight: formData.morning_sunlight,
            minutes: formData.sunlight_minutes || 0,
            location: formData.sunlight_location
          }, {
            onConflict: 'user_id,date',
            ignoreDuplicates: false
          });

        // Sunlight log saved or failed silently
      }

      setHasCompletedToday(true);
      
      // Show confetti for goal achievements
      if ((formData.water_intake && formData.water_intake >= (formData.water_target || 2000)) ||
          (formData.morning_sunlight && formData.sunlight_minutes && formData.sunlight_minutes >= 5)) {
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 3000);
      }

      toast({
        title: `${checkInType === 'morning' ? 'Morning' : 'Evening'} check-in saved!`,
        description: checkInType === 'morning' 
          ? "Your healthy habits have been logged. Come back this evening for daily reflection!"
          : "Your daily reflection is complete. Well done!"
      });
      
      if (onComplete) onComplete();
      
    } catch (error) {
      // Error saving check-in
      toast({
        title: "Error saving check-in",
        description: "Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      saveCheckIn();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  if (hasCompletedToday) {
    return (
      <Card className="p-8 text-center bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
        <CheckCircle className="w-16 h-16 text-primary mx-auto mb-4" />
        <h3 className="text-2xl font-bold text-foreground mb-2">Today's Health Check-In Complete!</h3>
        <p className="text-muted-foreground mb-6">
          {checkInType === 'morning' 
            ? "Morning habits logged! Come back this evening for your daily reflection and regulation ritual." 
            : "All done for today! You've completed both your morning habits and evening reflection."}
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          {formData.energy_level && (
            <div className="bg-background/50 p-3 rounded-lg">
              <Waves className="w-5 h-5 text-primary mx-auto mb-1" />
              <p className="font-medium">Energy</p>
              <p>{formData.energy_level}/10</p>
            </div>
          )}
          {formData.water_intake && (
            <div className="bg-background/50 p-3 rounded-lg">
              <Droplets className="w-5 h-5 text-primary mx-auto mb-1" />
              <p className="font-medium">Water Intake</p>
              <p>{formData.water_intake}ml</p>
            </div>
          )}
          {formData.morning_sunlight && (
            <div className="bg-background/50 p-3 rounded-lg">
              <Sunrise className="w-5 h-5 text-primary mx-auto mb-1" />
              <p className="font-medium">Morning Sun</p>
              <p>✅ {formData.sunlight_minutes || 0}min</p>
            </div>
          )}
          {formData.sleep_quality && (
            <div className="bg-background/50 p-3 rounded-lg">
              <Moon className="w-5 h-5 text-primary mx-auto mb-1" />
              <p className="font-medium">Sleep</p>
              <p>{formData.sleep_quality}/10</p>
            </div>
          )}
        </div>
        
        {/* Switch between morning and evening */}
        <div className="mt-6">
          <Button
            variant="outline"
            onClick={() => {
              setCheckInType(checkInType === 'morning' ? 'evening' : 'morning');
              setHasCompletedToday(false);
              setCurrentStep(0);
            }}
            className="flex items-center gap-2"
          >
            {checkInType === 'morning' ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
            Switch to {checkInType === 'morning' ? 'Evening' : 'Morning'} Check-In
          </Button>
        </div>
      </Card>
    );
  }

  // Helper function: Get activity suggestions based on difficulty and category
  const getSuggestedActivities = (difficulty: string, category: string): string[] => {
    const suggestions: Record<string, Record<string, string[]>> = {
      pleasant: {
        micro: [
          'Listen to one favourite song',
          'Look at old photos for 5 minutes',
          'Step outside and breathe for 2 minutes',
          'Pet an animal',
          'Light a candle and watch it'
        ],
        small: [
          'Watch an episode of a show you love',
          'Make a favourite simple snack',
          'Take a short walk around the block',
          'Read a chapter of a book',
          'Do a 10-minute hobby'
        ],
        stretch: [
          'Go to a place you used to enjoy',
          'Cook a full meal you love',
          'Spend an hour on a hobby',
          'Visit a museum or park',
          'Attend a class or event'
        ]
      },
      people: {
        micro: [
          'Text someone you care about',
          'Like/comment on a friend\'s social media',
          'Smile and say hi to a neighbour',
          'Send a quick voice message',
          'Write a thank you note'
        ],
        small: [
          'Have a phone call with a friend',
          'Meet someone for coffee',
          'Join an online community chat',
          'Attend a support group',
          'Play a game online with others'
        ],
        stretch: [
          'Meet up with someone in person',
          'Attend a social event',
          'Reconnect with someone you\'ve lost touch with',
          'Invite someone over',
          'Join a regular group (class, club, etc.)'
        ]
      },
      purpose: {
        micro: [
          'Make your bed',
          'Organise one drawer',
          'Water plants',
          'Write one journal sentence',
          'Complete one small chore'
        ],
        small: [
          'Clean/organise one room',
          'Work on a small project',
          'Learn something new for 20 minutes',
          'Help someone briefly',
          'Practice a skill'
        ],
        stretch: [
          'Work on a meaningful goal for an hour',
          'Volunteer',
          'Create something from scratch',
          'Tackle a challenging task',
          'Start a new meaningful project'
        ]
      }
    };
    
    return suggestions[category]?.[difficulty] || [];
  };

  // Helper function: Get opposite action suggestions based on emotion
  const getOppositeActions = (emotion: string): string[] => {
    const opposites: Record<string, string[]> = {
      anxious: [
        'Do something grounding (5 senses exercise)',
        'Engage in gentle movement',
        'Connect with someone calming',
        'Do something slow and deliberate',
        'Practice breathwork'
      ],
      depressed: [
        'Do something energizing (even just standing up)',
        'Get natural light',
        'Move your body gently',
        'Connect with someone positive',
        'Do something that used to bring joy'
      ],
      overwhelmed: [
        'Do ONE small thing (not everything)',
        'Take a break intentionally',
        'Ask for help with one task',
        'Simplify your expectations',
        'Do something calming'
      ],
      isolated: [
        'Reach out to someone (even digitally)',
        'Go somewhere people are',
        'Join an online community',
        'Pet an animal',
        'Watch something that makes you feel connected'
      ],
      unworthy: [
        'Do something you\'re good at',
        'Help someone else',
        'List 3 things you like about yourself',
        'Do self-care (you deserve it)',
        'Treat yourself kindly'
      ]
    };
    
    return opposites[emotion] || [];
  };

  const renderMorningStep = () => {
    // Rendering morning step
    const stepName = morningSteps[currentStep];

    // Connected Self - always first step
    if (currentStep === 0) {
      return (
        <ConnectedSelf
          initialData={formData}
          onDataChange={(data) => {
            Object.entries(data).forEach(([key, value]) => {
              handleInputChange(key as keyof CheckInData, value);
            });
          }}
        />
      );
    }

    // Dynamic habit-based steps
    if (stepName === 'Exercise & Movement') {
      return (
        <EnhancedExerciseTracker 
          mode="morning"
          initialData={formData}
          onDataChange={(data) => {
            Object.entries(data).forEach(([key, value]) => {
              handleInputChange(key as keyof CheckInData, value);
            });
          }}
        />
      );
    }
    
    if (stepName === 'Enhanced Sleep Tracker') {
      return (
        <EnhancedSleepTracker 
          initialData={formData}
          onDataChange={(data) => {
            Object.entries(data).forEach(([key, value]) => {
              handleInputChange(key as keyof CheckInData, value);
            });
          }}
        />
      );
    }

    if (stepName === 'Sunlight & Nature') {
      return (
        <div className="space-y-6">
          <div className="text-center">
            <Trees className="w-12 h-12 text-primary mx-auto mb-4" />
            <h3 className="text-2xl font-bold mb-2">🌞 Sunlight & Nature</h3>
            <p className="text-muted-foreground mb-6">Track your connection with natural light and outdoors</p>
          </div>
          
          <div className="space-y-6">
            <div className="p-4 border rounded-lg bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-950/20 dark:to-orange-950/20">
              <div className="flex items-center gap-3 mb-4">
                <Sunrise className={`w-8 h-8 transition-all duration-500 ${formData.morning_sunlight ? 'text-yellow-500 animate-pulse' : 'text-muted-foreground'}`} />
                <div>
                  <h4 className="font-semibold">Morning Sunlight Exposure</h4>
                  <p className="text-sm text-muted-foreground">Early sunlight helps melatonin production, mood, and sleep quality</p>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="morning_sunlight"
                    checked={formData.morning_sunlight || false}
                    onCheckedChange={(checked) => handleInputChange('morning_sunlight', checked)}
                  />
                  <Label htmlFor="morning_sunlight" className="font-medium">
                    I got sunlight within the first hour of waking
                  </Label>
                </div>

                {formData.morning_sunlight && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-medium mb-2 block">Minutes (min 5-10 recommended)</Label>
                        <Input
                          type="number"
                          placeholder="10"
                          value={formData.sunlight_minutes || ''}
                          onChange={(e) => handleInputChange('sunlight_minutes', parseInt(e.target.value) || 0)}
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-medium mb-2 block">Location</Label>
                        <Select value={formData.sunlight_location} onValueChange={(value) => handleInputChange('sunlight_location', value)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Where?" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="outside">Outside</SelectItem>
                            <SelectItem value="window">By window</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div>
              <Label className="text-lg font-semibold mb-4 block">Time spent in nature today (minutes)</Label>
              <Input
                type="number"
                placeholder="Walk in park, garden time, outdoor exercise..."
                value={formData.nature_time_minutes || ''}
                onChange={(e) => handleInputChange('nature_time_minutes', parseInt(e.target.value) || 0)}
              />
              <p className="text-sm text-muted-foreground mt-2">Include walks, gardening, outdoor activities</p>
            </div>
          </div>
        </div>
      );
    }

    if (stepName === 'Hydration Tracker') {
      return (
        <WaterTracker
          initialIntake={formData.water_intake || 0}
          target={formData.water_target || 2000}
          glassSize={formData.glass_size || 250}
          onUpdate={(intake, target, glassSize) => {
            handleInputChange('water_intake', intake);
            handleInputChange('water_target', target);
            handleInputChange('glass_size', glassSize);
          }}
        />
      );
    }

    if (stepName === 'Food & Nutrition') {
      return (
          <div className="space-y-6">
            <div className="text-center">
              <Utensils className="w-12 h-12 text-primary mx-auto mb-4" />
              <h3 className="text-2xl font-bold mb-2">🍎 Food & Nutrition</h3>
              <p className="text-muted-foreground mb-6">Reflect on your eating patterns today</p>
            </div>
            
            <div className="space-y-6">
              <div>
                <Label className="text-lg font-semibold mb-4 block">Level of processed foods today</Label>
                <div className="px-4">
                  <Slider
                    value={[formData.processed_food_level || 5]}
                    onValueChange={(value) => handleInputChange('processed_food_level', value[0])}
                    max={10}
                    min={1}
                    step={1}
                    className="w-full"
                  />
                  <div className="flex justify-between text-sm text-muted-foreground mt-2">
                    <span>Mostly whole foods</span>
                    <span>Very processed</span>
                  </div>
                  <div className="text-center mt-2 font-medium">{formData.processed_food_level || 5}/10</div>
                </div>
              </div>

              <div>
                <Label className="text-lg font-semibold mb-4 block">Level of healthy/nourishing foods today</Label>
                <div className="px-4">
                  <Slider
                    value={[formData.healthy_food_level || 5]}
                    onValueChange={(value) => handleInputChange('healthy_food_level', value[0])}
                    max={10}
                    min={1}
                    step={1}
                    className="w-full"
                  />
                  <div className="flex justify-between text-sm text-muted-foreground mt-2">
                    <span>Poor nutrition</span>
                    <span>Very nourishing</span>
                  </div>
                  <div className="text-center mt-2 font-medium">{formData.healthy_food_level || 5}/10</div>
                </div>
              </div>

              <div>
                <Label className="text-lg font-semibold mb-4 block">Level of food cravings today</Label>
                <div className="px-4">
                  <Slider
                    value={[formData.cravings_level || 5]}
                    onValueChange={(value) => handleInputChange('cravings_level', value[0])}
                    max={10}
                    min={1}
                    step={1}
                    className="w-full"
                  />
                  <div className="flex justify-between text-sm text-muted-foreground mt-2">
                    <span>No cravings</span>
                    <span>Intense cravings</span>
                  </div>
                  <div className="text-center mt-2 font-medium">{formData.cravings_level || 5}/10</div>
                </div>
              </div>

              <div>
                <Label className="text-lg font-semibold mb-4 block">Level of impulsive eating today</Label>
                <div className="px-4">
                  <Slider
                    value={[formData.impulsive_eating_level || 5]}
                    onValueChange={(value) => handleInputChange('impulsive_eating_level', value[0])}
                    max={10}
                    min={1}
                    step={1}
                    className="w-full"
                  />
                  <div className="flex justify-between text-sm text-muted-foreground mt-2">
                    <span>Very mindful</span>
                    <span>Very impulsive</span>
                  </div>
                  <div className="text-center mt-2 font-medium">{formData.impulsive_eating_level || 5}/10</div>
                </div>
              </div>

              {/* Optional eating disorder support */}
              <div className="border-t pt-4">
                <div className="flex items-center space-x-2 mb-4">
                  <Checkbox
                    id="eating_disorder_support"
                    checked={formData.eating_disorder_support || false}
                    onCheckedChange={(checked) => handleInputChange('eating_disorder_support', checked)}
                  />
                  <Label htmlFor="eating_disorder_support" className="font-medium">
                    I would like eating disorder recovery support options
                  </Label>
                </div>

                {formData.eating_disorder_support && (
                  <div className="space-y-3 p-4 border rounded-lg bg-muted/20">
                    <p className="text-sm text-muted-foreground mb-3">Optional tracking for recovery support:</p>
                    
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="purging_behavior"
                        checked={formData.purging_behavior || false}
                        onCheckedChange={(checked) => handleInputChange('purging_behavior', checked)}
                      />
                      <Label htmlFor="purging_behavior" className="text-sm">Purging behaviour today</Label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="restriction_behavior"
                        checked={formData.restriction_behavior || false}
                        onCheckedChange={(checked) => handleInputChange('restriction_behavior', checked)}
                      />
                      <Label htmlFor="restriction_behavior" className="text-sm">Food restriction today</Label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="laxative_use"
                        checked={formData.laxative_use || false}
                        onCheckedChange={(checked) => handleInputChange('laxative_use', checked)}
                      />
                      <Label htmlFor="laxative_use" className="text-sm">Laxative use today</Label>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
    }

    if (stepName === 'Alcohol Tracking') {
      return (
        <AlcoholTracker
            initialData={{
              units_consumed: formData.units_consumed || 0,
              drinks: formData.drinks || [],
              risk_level: formData.risk_level || 'green',
              ...formData
            }}
            supportMode={true}
            onDataChange={(data) => {
              Object.entries(data).forEach(([key, value]) => {
                handleInputChange(key as keyof CheckInData, value);
              });
            }}
          />
        );
    }

    if (stepName === 'Habit Tracker') {
      return (
          <div className="space-y-6">
            {/* Habit Type Selector */}
            <div className="text-center mb-6">
              <div className="mb-4">
                <Cigarette className="w-12 h-12 text-primary mx-auto mb-4" />
                <h3 className="text-2xl font-bold mb-2">🎯 Habit Tracker</h3>
                <p className="text-muted-foreground">Choose which habit you want to focus on today</p>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-w-2xl mx-auto mb-6">
                <Button
                  variant={(formData.type === 'smoking' || !formData.type) ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleInputChange('type', 'smoking')}
                  className="flex items-center gap-2 justify-start p-3"
                >
                  <Cigarette className="w-4 h-4" />
                  Smoking
                </Button>
                <Button
                  variant={formData.type === 'caffeine' ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleInputChange('type', 'caffeine')}
                  className="flex items-center gap-2 justify-start p-3"
                >
                  <Coffee className="w-4 h-4" />
                  Caffeine
                </Button>
                <Button
                  variant={formData.type === 'screen_time' ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleInputChange('type', 'screen_time')}
                  className="flex items-center gap-2 justify-start p-3"
                >
                  <Smartphone className="w-4 h-4" />
                  Screen Time
                </Button>
                <Button
                  variant={formData.type === 'drugs' ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleInputChange('type', 'drugs')}
                  className="flex items-center gap-2 justify-start p-3"
                >
                  <Pill className="w-4 h-4" />
                  Substances
                </Button>
                <Button
                  variant={formData.type === 'shopping' ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleInputChange('type', 'shopping')}
                  className="flex items-center gap-2 justify-start p-3"
                >
                  <Plus className="w-4 h-4" />
                  Shopping
                </Button>
                <Button
                  variant={formData.type === 'gaming' ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleInputChange('type', 'gaming')}
                  className="flex items-center gap-2 justify-start p-3"
                >
                  <Activity className="w-4 h-4" />
                  Gaming
                </Button>
              </div>
            </div>
            
            {/* Habit Tracker Component */}
            {formData.type && (
              <HabitTracker 
                habitType={formData.type as 'smoking' | 'drugs' | 'caffeine' | 'screen_time' | 'shopping' | 'gaming'}
                supportMode={true}
                initialData={{
                  type: formData.type,
                  support_mode: true,
                  ...formData
                }}
                onDataChange={(data) => {
                  Object.entries(data).forEach(([key, value]) => {
                    handleInputChange(key as keyof CheckInData, value);
                  });
                }}
              />
            )}
            
            {!formData.type && (
              <div className="text-center p-8 bg-muted/30 rounded-lg">
                <Target className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">
                  Select a habit above to start tracking your progress and get personalised support tools.
                </p>
              </div>
            )}
          </div>
        );
    }

    if (stepName === 'Expanding Your Life') {
      return (
          <div className="space-y-6">
            {/* Life Expansion Calendar Component */}
            <LifeExpansionCalendar />
            
            <Card className="p-6 space-y-6">
              <div className="text-center">
                <Target className="w-12 h-12 text-primary mx-auto mb-4" />
                <h3 className="text-2xl font-bold mb-2">🎯 Plan Today's Expansion</h3>
                <p className="text-muted-foreground mb-6">Depression shrinks life. Let's add one thing back today.</p>
              </div>

              {/* Collapsible Psychoeducation Card */}
              <div className="p-4 bg-gradient-to-r from-primary/10 to-accent/10 rounded-lg border border-primary/20">
                <div className="space-y-3">
                  <h4 className="font-semibold text-lg flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-primary" />
                    Why Life Expansion Matters
                  </h4>
                  <div className="text-sm space-y-2 text-muted-foreground">
                    <p><strong>Reconnect:</strong> Notice what depression has slowly taken from you. Awareness is the first step.</p>
                    <p><strong>Release:</strong> Let go of "all or nothing" thinking. A micro win is still a win.</p>
                    <p><strong>Reprogram:</strong> Rebuild your life one small step at a time. Activity precedes motivation.</p>
                  </div>
                  <details className="mt-3">
                    <summary className="cursor-pointer text-sm font-medium text-primary hover:underline">View Research</summary>
                    <ul className="mt-2 text-xs space-y-1 list-disc list-inside text-muted-foreground">
                      <li>Behavioural Activation is as effective as medication for mild-moderate depression</li>
                      <li>Activity scheduling rewires the brain's reward circuits</li>
                      <li>Graduated goals prevent overwhelm and build self-efficacy</li>
                      <li>Social connection triggers oxytocin (natural mood regulator)</li>
                    </ul>
                  </details>
                </div>
              </div>

            {/* Activity Category Selection */}
            <div className="space-y-4">
              <Label className="text-lg font-semibold">What's Missing from Your Life?</Label>
              <p className="text-sm text-muted-foreground">Depression slowly takes things away. Let's add one back today.</p>
              
              <div className="grid grid-cols-3 gap-3">
                <Button
                  variant={activityCategory === 'pleasant' ? 'default' : 'outline'}
                  onClick={() => setActivityCategory('pleasant')}
                  className="h-auto py-4 flex flex-col items-center gap-2"
                >
                  <Sparkles className="w-5 h-5" />
                  <span>Pleasant Activities</span>
                </Button>
                <Button
                  variant={activityCategory === 'people' ? 'default' : 'outline'}
                  onClick={() => setActivityCategory('people')}
                  className="h-auto py-4 flex flex-col items-center gap-2"
                >
                  <Users className="w-5 h-5" />
                  <span>People Connections</span>
                </Button>
                <Button
                  variant={activityCategory === 'purpose' ? 'default' : 'outline'}
                  onClick={() => setActivityCategory('purpose')}
                  className="h-auto py-4 flex flex-col items-center gap-2"
                >
                  <Target className="w-5 h-5" />
                  <span>Purpose & Mastery</span>
                </Button>
              </div>
            </div>

            {/* Difficulty Selection */}
            <div className="space-y-3">
              <Label>Choose Your Starting Point:</Label>
              <RadioGroup value={activityDifficulty} onValueChange={(value: any) => setActivityDifficulty(value)}>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2 p-3 border rounded hover:bg-accent cursor-pointer">
                    <RadioGroupItem value="micro" id="micro" />
                    <Label htmlFor="micro" className="flex-1 cursor-pointer">
                      <div className="font-medium">Micro Win (5-15 min)</div>
                      <div className="text-sm text-muted-foreground">Small, achievable actions</div>
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2 p-3 border rounded hover:bg-accent cursor-pointer">
                    <RadioGroupItem value="small" id="small" />
                    <Label htmlFor="small" className="flex-1 cursor-pointer">
                      <div className="font-medium">Small Win (15-45 min)</div>
                      <div className="text-sm text-muted-foreground">Moderate effort activities</div>
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2 p-3 border rounded hover:bg-accent cursor-pointer">
                    <RadioGroupItem value="stretch" id="stretch" />
                    <Label htmlFor="stretch" className="flex-1 cursor-pointer">
                      <div className="font-medium">Stretch Win (45+ min)</div>
                      <div className="text-sm text-muted-foreground">Meaningful challenges</div>
                    </Label>
                  </div>
                </div>
              </RadioGroup>
            </div>

            {/* Activity Suggestions */}
            <div className="p-4 bg-accent/20 rounded border">
              <Label className="font-medium mb-2 block">Suggestions:</Label>
              <div className="flex flex-wrap gap-2">
                {getSuggestedActivities(activityDifficulty, activityCategory).map((activity, idx) => (
                  <Button
                    key={idx}
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setPlannedActivity(activity);
                      handleInputChange('life_expansion_planned_activity', activity);
                      handleInputChange('life_expansion_activity_category', activityCategory);
                      handleInputChange('life_expansion_difficulty', activityDifficulty);
                    }}
                    className="text-xs"
                  >
                    {activity}
                  </Button>
                ))}
              </div>
            </div>

            {/* Activity Input */}
            {activityCategory === 'people' ? (
              <div className="space-y-4">
                <div>
                  <Label>Who Will You Connect With?</Label>
                  <Input
                    value={socialPerson}
                    onChange={(e) => {
                      setSocialPerson(e.target.value);
                      handleInputChange('life_expansion_social_person', e.target.value);
                    }}
                    placeholder="Person's name or type of connection..."
                  />
                </div>
                <div>
                  <Label>Connection Goal:</Label>
                  <Textarea
                    value={socialGoal}
                    onChange={(e) => {
                      setSocialGoal(e.target.value);
                      handleInputChange('life_expansion_social_goal', e.target.value);
                    }}
                    placeholder="e.g., 'Send a text checking in', 'Ask about their week'..."
                    rows={3}
                  />
                </div>
              </div>
            ) : (
              <div>
                <Label>What Will You Add Back Today?</Label>
                <Input
                  value={plannedActivity}
                  onChange={(e) => {
                    setPlannedActivity(e.target.value);
                    handleInputChange('life_expansion_planned_activity', e.target.value);
                    handleInputChange('life_expansion_activity_category', activityCategory);
                    handleInputChange('life_expansion_difficulty', activityDifficulty);
                  }}
                  placeholder="Describe the activity..."
                />
              </div>
            )}

            {/* Opposite to Emotion Section */}
            <div className="space-y-4 p-4 bg-accent/10 rounded border">
              <Label className="text-lg font-medium flex items-center gap-2">
                <Compass className="h-5 w-5 text-primary" />
                "Opposite to Emotion" Hack
              </Label>
              <p className="text-sm text-muted-foreground">
                Your feelings are valid, but you don't have to follow their instructions.
              </p>
              
              <div>
                <Label>What emotion is telling you to avoid things?</Label>
                <Select value={oppositeEmotion} onValueChange={(value) => {
                  setOppositeEmotion(value);
                  handleInputChange('life_expansion_opposite_emotion', value);
                }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="anxious">Anxious / Worried</SelectItem>
                    <SelectItem value="depressed">Depressed / Hopeless</SelectItem>
                    <SelectItem value="overwhelmed">Overwhelmed / Exhausted</SelectItem>
                    <SelectItem value="isolated">Isolated / Alone</SelectItem>
                    <SelectItem value="unworthy">Unworthy / Inadequate</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {oppositeEmotion && (
                <div className="p-3 bg-primary/10 rounded">
                  <p className="text-sm font-medium mb-2">Opposite Actions:</p>
                  <div className="flex flex-wrap gap-2">
                    {getOppositeActions(oppositeEmotion).map((action, idx) => (
                      <Button
                        key={idx}
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          setMotivationHack(action);
                          handleInputChange('life_expansion_motivation_hack', action);
                        }}
                        className="text-xs"
                      >
                        {action}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Values Connection */}
            <div className="space-y-3">
              <Label className="text-lg font-medium flex items-center gap-2">
                <Heart className="h-5 w-5 text-accent" />
                Why This Matters to You
              </Label>
              <p className="text-sm text-muted-foreground">
                Connect this activity to your core values for deeper meaning.
              </p>
              
              <Select value={valuesAlignment} onValueChange={(value) => {
                setValuesAlignment(value);
                handleInputChange('values_activity_alignment', value);
              }}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a value..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="connection">Connection / Relationships</SelectItem>
                  <SelectItem value="growth">Personal Growth</SelectItem>
                  <SelectItem value="creativity">Creativity</SelectItem>
                  <SelectItem value="health">Health / Wellbeing</SelectItem>
                  <SelectItem value="contribution">Contributing to Others</SelectItem>
                  <SelectItem value="joy">Joy / Pleasure</SelectItem>
                  <SelectItem value="freedom">Freedom</SelectItem>
                  <SelectItem value="achievement">Achievement</SelectItem>
                  <SelectItem value="spirituality">Spirituality</SelectItem>
                  <SelectItem value="authenticity">Authenticity</SelectItem>
                </SelectContent>
              </Select>

              {valuesAlignment && (
                <div className="p-3 bg-accent/10 rounded border text-sm">
                  <strong>Remember:</strong> This isn't just a task. It's a step toward living in alignment with: <strong>{valuesAlignment}</strong>.
                </div>
              )}
            </div>
          </Card>
          </div>
        );
    }

    // Default fallback
    return null;
  };

  const renderEveningStep = () => {
    switch (currentStep) {
      case 0: // Daily Reflection
        return (
          <div className="space-y-6">
            <div className="text-center">
              <Moon className="w-12 h-12 text-primary mx-auto mb-4" />
              <h3 className="text-2xl font-bold mb-2">🌙 Daily Reflection</h3>
              <p className="text-muted-foreground mb-6">How was your day? What did you learn about yourself?</p>
            </div>
            
            <div className="space-y-6">
              <div>
                <Label className="text-lg font-semibold mb-4 block">What went well today?</Label>
                <Textarea
                  placeholder="Celebrate the wins, big and small. What made you smile? What are you proud of?"
                  value={formData.gratitude_kindness || ''}
                  onChange={(e) => handleInputChange('gratitude_kindness', e.target.value)}
                  className="min-h-[100px]"
                />
              </div>

              <div>
                <Label className="text-lg font-semibold mb-4 block">What would you do differently?</Label>
                <Textarea
                  placeholder="No judgment, just awareness. What patterns do you notice? What would you adjust?"
                  value={formData.future_self_action || ''}
                  onChange={(e) => handleInputChange('future_self_action', e.target.value)}
                  className="min-h-[100px]"
                />
              </div>
            </div>
          </div>
        );

      case 1: // Behaviour Insights
        return (
          <BehaviorInsights />
        );

      case 2: // Life Expansion Review
        return (
          <Card className="p-6 space-y-6">
            <div className="text-center">
              <CheckCircle className="w-12 h-12 text-primary mx-auto mb-4" />
              <h3 className="text-2xl font-bold mb-2">🌱 Life Expansion Review</h3>
              <p className="text-muted-foreground mb-6">Did you add something back to your life today?</p>
            </div>

            {/* Streak Display */}
            <div className="flex items-center justify-between p-4 bg-gradient-to-r from-primary/20 to-accent/20 rounded-lg">
              <div>
                <h3 className="text-2xl font-bold">{formData.life_expansion_streak_count || 0} Day Streak</h3>
                <p className="text-sm opacity-80">Keep expanding your life!</p>
              </div>
              <div className="text-4xl">🔥</div>
            </div>

            {/* Today's Plan Recall */}
            {formData.life_expansion_planned_activity && (
              <div className="p-4 bg-accent/10 rounded border">
                <Label className="font-medium mb-2 block">This Morning's Plan:</Label>
                <div className="space-y-1 text-sm">
                  <p><strong>Activity:</strong> {formData.life_expansion_planned_activity}</p>
                  <p><strong>Difficulty:</strong> {formData.life_expansion_difficulty} win</p>
                  <p><strong>Category:</strong> {formData.life_expansion_activity_category}</p>
                  {formData.values_activity_alignment && <p><strong>Value:</strong> {formData.values_activity_alignment}</p>}
                </div>
              </div>
            )}

            {/* Completion Status */}
            <div className="space-y-4">
              <Label className="text-lg font-semibold">Did You Add Something Back to Your Life Today?</Label>
              
              <RadioGroup value={completionStatus} onValueChange={(value) => {
                setCompletionStatus(value);
                handleInputChange('life_expansion_completion_status', value);
              }}>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2 p-3 border rounded hover:bg-accent cursor-pointer">
                    <RadioGroupItem value="completed" id="completed" />
                    <Label htmlFor="completed" className="flex-1 cursor-pointer">
                      <div className="font-medium text-green-600">✅ Yes! I did it!</div>
                      <div className="text-sm text-muted-foreground">Completed as planned</div>
                    </Label>
                  </div>
                  
                  <div className="flex items-center space-x-2 p-3 border rounded hover:bg-accent cursor-pointer">
                    <RadioGroupItem value="partial" id="partial" />
                    <Label htmlFor="partial" className="flex-1 cursor-pointer">
                      <div className="font-medium text-blue-600">🎯 Partial Success</div>
                      <div className="text-sm text-muted-foreground">Did part of it, or something similar</div>
                    </Label>
                  </div>
                  
                  <div className="flex items-center space-x-2 p-3 border rounded hover:bg-accent cursor-pointer">
                    <RadioGroupItem value="not_attempted" id="not_attempted" />
                    <Label htmlFor="not_attempted" className="flex-1 cursor-pointer">
                      <div className="font-medium text-orange-600">⏸️ Didn't Try</div>
                      <div className="text-sm text-muted-foreground">Wasn't able to attempt it</div>
                    </Label>
                  </div>
                  
                  <div className="flex items-center space-x-2 p-3 border rounded hover:bg-accent cursor-pointer">
                    <RadioGroupItem value="barrier" id="barrier" />
                    <Label htmlFor="barrier" className="flex-1 cursor-pointer">
                      <div className="font-medium text-red-600">🚧 Hit a Barrier</div>
                      <div className="text-sm text-muted-foreground">Something got in the way</div>
                    </Label>
                  </div>
                </div>
              </RadioGroup>
            </div>

            {/* Conditional Content Based on Status */}
            {completionStatus === 'completed' && (
              <div className="p-6 bg-gradient-to-br from-primary/20 to-accent/20 rounded-lg border-2 border-primary space-y-4">
                <div className="text-center">
                  <div className="text-6xl mb-2">🎉</div>
                  <h3 className="text-2xl font-bold text-primary mb-2">You Did It!</h3>
                  <p className="text-sm">You added something back to your life today. That's courage in action.</p>
                </div>
                
                <div>
                  <Label>How did it feel?</Label>
                  <Textarea
                    value={howItFelt}
                    onChange={(e) => {
                      setHowItFelt(e.target.value);
                      handleInputChange('life_expansion_how_it_felt', e.target.value);
                    }}
                    placeholder="Describe the experience... Was it hard? Easier than expected? How do you feel now?"
                    rows={4}
                  />
                </div>
              </div>
            )}

            {(completionStatus === 'partial' || completionStatus === 'not_attempted' || completionStatus === 'barrier') && completionStatus && (
              <div className="p-6 bg-accent/10 rounded-lg border space-y-4">
                <div>
                  <Label>What got in the way?</Label>
                  <Textarea
                    value={barrier}
                    onChange={(e) => {
                      setBarrier(e.target.value);
                      handleInputChange('life_expansion_barrier', e.target.value);
                    }}
                    placeholder="No judgment. What made it hard? (energy, anxiety, time, logistics, etc.)"
                    rows={3}
                  />
                </div>
                
                <div>
                  <Label>What could help next time?</Label>
                  <Textarea
                    value={barrierSolution}
                    onChange={(e) => {
                      setBarrierSolution(e.target.value);
                      handleInputChange('life_expansion_barrier_solution', e.target.value);
                    }}
                    placeholder="Smaller goal? Different time of day? Ask for support?"
                    rows={3}
                  />
                </div>
                
                <div className="p-4 bg-blue-50 dark:bg-blue-950 rounded border border-blue-200 dark:border-blue-800">
                  <p className="text-sm">
                    <strong>Remember:</strong> Noticing barriers is progress. Every "no" teaches you what "yes" needs.
                  </p>
                </div>
              </div>
            )}
          </Card>
        );

      case 3: // Regulation Ritual - Evening
        return (
          <div className="space-y-6">
            <div className="text-center">
              <Sparkles className="w-12 h-12 text-primary mx-auto mb-4" />
              <h3 className="text-2xl font-bold mb-2">🧘‍♀️ Evening Regulation Ritual</h3>
              <p className="text-muted-foreground mb-6">Which practices helped you regulate and wind down today?</p>
            </div>
            
            <div className="space-y-6">
              <div>
                <Label className="text-lg font-semibold mb-4 block">Regulation practices used today</Label>
                <RadioGroup value={formData.regulation_ritual} onValueChange={(value) => handleInputChange('regulation_ritual', value)}>
                  <div className="grid grid-cols-2 gap-3">
                    {['breathwork', 'meditation', 'EFT', 'movement', 'journaling', 'none'].map((option) => (
                      <div key={option} className="flex items-center space-x-2 p-3 rounded-lg border hover:bg-muted/50">
                        <RadioGroupItem value={option} id={`ritual-${option}`} />
                        <Label htmlFor={`ritual-${option}`} className="capitalize cursor-pointer flex-1">
                          {option}
                        </Label>
                      </div>
                    ))}
                  </div>
                </RadioGroup>
              </div>

              <div>
                <Label className="text-lg font-semibold mb-4 block">Add up to 2 custom regulation rituals</Label>
                <div className="space-y-3">
                  {[0, 1].map((index) => (
                    <div key={index} className="flex items-center gap-3">
                      <Input
                        placeholder={`Custom ritual ${index + 1} (e.g., warm bath, tea ceremony, stretching)`}
                        value={customRituals[index] || ''}
                        onChange={(e) => {
                          const newRituals = [...customRituals];
                          newRituals[index] = e.target.value;
                          setCustomRituals(newRituals);
                          handleInputChange('custom_regulation_rituals', newRituals);
                        }}
                      />
                      <Checkbox
                        checked={(formData.regulation_rituals_completed as string[] || []).includes(customRituals[index])}
                        onCheckedChange={(checked) => {
                          const completed = (formData.regulation_rituals_completed as string[] || []);
                          if (checked) {
                            handleInputChange('regulation_rituals_completed', [...completed, customRituals[index]]);
                          } else {
                            handleInputChange('regulation_rituals_completed', completed.filter(r => r !== customRituals[index]));
                          }
                        }}
                        disabled={!customRituals[index]}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 4: // Intuitive Wisdom
        return (
          <div className="space-y-6">
            <div className="text-center">
              <Compass className="w-12 h-12 text-primary mx-auto mb-4" />
              <h3 className="text-2xl font-bold mb-2">🕊️ Intuitive Wisdom</h3>
              <p className="text-muted-foreground mb-6">What is your inner wisdom sharing with you tonight?</p>
            </div>
            
            <div className="space-y-6">
              <div>
                <Label className="text-lg font-semibold mb-4 block">What is your intuition telling you right now?</Label>
                <Textarea
                  placeholder="Trust what comes up... your inner wisdom knows. What guidance, insights, or nudges are you receiving?"
                  value={formData.intuitive_nudge || ''}
                  onChange={(e) => handleInputChange('intuitive_nudge', e.target.value)}
                  className="min-h-[120px]"
                />
              </div>

              <div>
                <Label className="text-lg font-semibold mb-4 block">Tomorrow's intention</Label>
                <Textarea
                  placeholder="What energy do you want to carry into tomorrow? How do you want to show up?"
                  value={formData.manifestation_custom || ''}
                  onChange={(e) => handleInputChange('manifestation_custom', e.target.value)}
                  className="min-h-[100px]"
                />
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Show preferences selector if requested or if no preferences set
  if (showPreferencesSelector) {
    return (
      <Card className="max-w-4xl mx-auto p-8">
        <HabitPreferencesSelector 
          onSave={() => {
            setShowPreferencesSelector(false);
            loadHabitPreferences();
            toast({
              title: "Preferences saved!",
              description: "Your daily check-in has been customised to your selected habits."
            });
          }}
          onClose={habitPreferences.length > 0 ? () => setShowPreferencesSelector(false) : undefined}
        />
      </Card>
    );
  }

  return (
    <Card className="max-w-4xl mx-auto p-8">
      {/* Confetti Animation */}
      {showConfetti && (
        <div className="fixed inset-0 pointer-events-none z-50">
          <div className="animate-pulse text-6xl text-center mt-20">🎉</div>
        </div>
      )}

      {/* Check-in Type Selector */}
      <div className="mb-6 text-center">
        <div className="flex items-center justify-center gap-4 mb-4">
          <Button
            variant={checkInType === 'morning' ? 'default' : 'outline'}
            onClick={() => {setCheckInType('morning'); setCurrentStep(0);}}
            className="flex items-center gap-2"
          >
            <Sunrise className="w-4 h-4" />
            Morning Check-In
          </Button>
          <Button
            variant={checkInType === 'evening' ? 'default' : 'outline'}
            onClick={() => {setCheckInType('evening'); setCurrentStep(0);}}
            className="flex items-center gap-2"
          >
            <Moon className="w-4 h-4" />
            Evening Reflection
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowPreferencesSelector(true)}
            className="flex items-center gap-2 ml-auto"
          >
            <Settings className="w-4 h-4" />
            Manage Habits
          </Button>
        </div>
        
        <div className="text-center p-4 bg-muted/30 rounded-lg">
          <div className="flex items-center justify-center gap-2 mb-2">
            <h2 className="text-2xl font-bold">Healthy Habits Check-In</h2>
            <SparkleGuide
              title="How to use Daily Check-Ins"
              items={[
                { title: 'Morning check-in', description: 'Log habits, set intentions, and plan your movement for the day.' },
                { title: 'Evening reflection', description: 'Review what went well, process emotions, and prepare for restful sleep.' },
                { title: 'Track patterns', description: 'Over time, see correlations between sleep, exercise, mood, and more.' },
                { title: 'Customise habits', description: 'Use "Manage Habits" to choose which areas to track.' }
              ]}
            />
          </div>
          <p className="text-muted-foreground">
            {checkInType === 'morning' 
              ? "Start your day with intention - track your wellness habits and set the tone for a nourishing day"
              : "End your day with reflection - process your experiences and prepare for restorative sleep"}
          </p>
        </div>
        
        {/* WHOOP Auto-Fill Indicator */}
        {checkInType === 'morning' && whoopConnected && (
          <WhoopAutoFillIndicator
            isConnected={whoopConnected}
            hasData={whoopHasData}
            whoopData={whoopData || null}
            lastSync={whoopLastSync}
            className="mt-4"
          />
        )}
      </div>

      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary" />
            <span className="text-sm text-muted-foreground">
              Step {currentStep + 1} of {steps.length}
            </span>
          </div>
        </div>
        
        <div className="w-full bg-muted rounded-full h-2 mb-2">
          <div 
            className="bg-primary h-2 rounded-full transition-all duration-300 ease-out"
            style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
          />
        </div>
        
        <div className="flex justify-between text-xs text-muted-foreground">
          {steps.map((step, index) => (
            <button
              key={step}
              onClick={() => setCurrentStep(index)}
              className={`cursor-pointer hover:text-primary transition-colors ${
                index <= currentStep ? 'text-primary font-medium' : ''
              } ${index === currentStep ? 'underline' : ''}`}
              title={`Jump to ${step}`}
            >
              {step}
            </button>
          ))}
        </div>
      </div>

      {/* Quick Navigation only shown on initial dashboard view */}

      {/* Step Content */}
      <div className="min-h-[500px]">
        {checkInType === 'morning' ? renderMorningStep() : renderEveningStep()}
      </div>

      {/* Navigation */}
      <div className="flex justify-between mt-8 pt-6 border-t">
        <Button
          variant="outline"
          onClick={prevStep}
          disabled={currentStep === 0}
          className="px-8"
        >
          Previous
        </Button>
        
        <Button
          onClick={nextStep}
          disabled={isLoading}
          className="px-8"
        >
          {currentStep === steps.length - 1 ? `Complete ${checkInType === 'morning' ? 'Morning' : 'Evening'} Check-In` : 'Next'}
        </Button>
      </div>
    </Card>
  );
}